import { createSelector } from 'reselect'
import {
  get,
  some,
  reduce,
  transform,
  mapValues,
  isNull,
  isUndefined,
  isArray,
  includes,
  findKey,
  find,
  each,
  isEmpty,
  remove,
  sortBy,
} from 'lodash-es'

import { formatOrdinalNum, intToBool } from '../sharedLegacy/utils'

const webinarPerformanceOrdinalFormatter = (
  num,
  base = 'best performing webinar'
) => {
  const position = formatOrdinalNum(num)

  return position === '1st' ? `the ${base}` : `${position} ${base}`
}

const getTracks = (state) => state.webinar.tracksByParticipant
const getLocalTracksState = (state) => state.webinar.localTracks
const getRole = (state) => state.webinar.role
const getSelectedPresenterTrackId = (state) =>
  state.webinar.selectedPresenterTrackId
const getSelectedPresenterParticipantId = (state) =>
  state.webinar.selectedPresenterParticipantId
const getLiveTriggeredTimestamp = (state) =>
  state.webinar.liveTogglingTriggeredAt
const getEndingWebinarState = (state) => state.webinar.isEndingWebinar
const getLocalParticipantId = (state) => state.webinar.localParticipantId
const getParticipantIds = (state) => state.webinar.participantIds
const getFunnelData = (state) => state.webinar.funnelData
const getMetricsData = (state) => state.webinar.metricsData
const getLiveData = (state) => state.webinar.liveData
const getCriticalErrorType = (state) => state.webinar.criticalErrorType
const getGearModeState = (state) => state.webinar.isGearMode

const sortScreensharingFirst = (tracks) => {
  const tracksCopy = [...tracks]
  // mutates tracksCopy
  const screenTrackArr = remove(tracksCopy, { name: 'screensharing' })

  return [...screenTrackArr, ...tracksCopy]
}

const sortSecondCamLast = (a) => {
  if (includes(a.name, 'secondcam')) {
    return -1
  }

  return 1
}

const sortByKind = (track) => {
  return track.kind === 'video' ? -1 : 1
}

const filterPlaybackTracks = (track) => !includes(track.name, 'playbackVideo')

const getRemoteTracks = createSelector([getTracks], (tracksByParticipant) => {
  const sorted = mapValues(tracksByParticipant, (tracks) =>
    sortScreensharingFirst(tracks)
  )

  return sorted
})

const getLocalTracksWithoutPlayback = createSelector(
  [getLocalTracksState],
  (localTracks) => {
    return localTracks.filter(filterPlaybackTracks)
  }
)

export const getTracksByParticipant = createSelector(
  [getRemoteTracks],
  (tracksByParticipant) => {
    // remove video playback tracks
    return mapValues(tracksByParticipant, (tracks) => {
      return tracks.filter(filterPlaybackTracks)
    })
  }
)

const findFirstCamParticipant = (res, participantId) => {
  return findKey(res, (val, key) => includes(key, participantId))
}

const assignOriginParticipantId = (tracks, originParticipantId) => {
  tracks.forEach((track) => {
    // TODO (noahlaux): Refactor and keep reference in weakmap instead
    // eslint-disable-next-line no-param-reassign
    track.originParticipantId = originParticipantId
  })
}

export const getTracksByParticipantWithGroupedSecondCams = createSelector(
  [getTracksByParticipant, getLocalParticipantId],
  (tracksByParticipant, localParticipantId) => {
    const tracks = transform(tracksByParticipant, (res, val, key) => {
      const [, , , participantId] = key.split('|')
      const existingKey = findFirstCamParticipant(res, participantId)

      assignOriginParticipantId(val, key)

      if (localParticipantId && includes(localParticipantId, participantId)) {
        return
      }

      if (existingKey) {
        res[existingKey] = res[existingKey].concat(val)
      } else {
        res[key] = val
      }
    })

    const afterMap = mapValues(tracks, (singleParticipantsTracks) =>
      sortBy(singleParticipantsTracks, [sortSecondCamLast, sortByKind])
    )

    return afterMap
  }
)

export const getLocalTracks = createSelector(
  [getLocalTracksState],
  (localTracks) => sortScreensharingFirst(localTracks)
)

export const getLocalTracksWithGroupedSecondCams = createSelector(
  [
    getLocalTracksWithoutPlayback,
    getLocalParticipantId,
    getTracksByParticipant,
  ],
  (localTracks, localParticipantId, tracksByParticipant) => {
    assignOriginParticipantId(localTracks, 'local')

    if (isNull(localParticipantId)) {
      return localTracks
    }

    const [, , , participantId] = localParticipantId.split('|')
    let tracks = [...localTracks]

    each(tracksByParticipant, (val, key) => {
      if (includes(key, participantId)) {
        assignOriginParticipantId(val, key)
        tracks = [...val, ...tracks]
      }
    })

    return sortScreensharingFirst(tracks)
  }
)

export const hasLocalSecondCam = createSelector(
  [getLocalTracksWithGroupedSecondCams],
  (localTracksWithSecondCam) =>
    find(localTracksWithSecondCam, (track) => includes(track.name, 'secondcam'))
)

export const isBroadcastStreaming = createSelector(
  (state) => state.webinar.webinarInfo.streaming_p,
  (isStreaming) => intToBool(isStreaming)
)

export const isBroadcastLive = createSelector(
  (state) => state.webinar.webinarInfo.broadcasting_p,
  (isBroadcasting) => intToBool(isBroadcasting)
)

export const isBroadcastRecording = createSelector(
  (state) => state.webinar.webinarInfo.recording_p,
  (isRecording) => intToBool(isRecording)
)

export const getCurrentViewers = createSelector(
  (state) => state.webinar.webinarInfo.current_viewers,
  (currentViwers) => {
    if (isEmpty(currentViwers)) return null
    return Number(currentViwers) || null
  }
)

export const shouldShowGearModePlayer = createSelector(
  [
    isBroadcastStreaming,
    (state) => state.webinar.isGearModePreviewInitialising,
  ],
  (isStreaming, isGearModePreviewInitialising) => {
    return isStreaming && !isGearModePreviewInitialising
  }
)

export const getTimerBroadcastingProps = createSelector(
  [
    isBroadcastLive,
    (state) => state.webinar.webinarInfo.broadcasting_start_time,
  ],
  (isBroadcasting, startTime) => {
    if (!isBroadcasting) {
      return undefined
    }

    return {
      broadcasting_p: isBroadcasting ? 1 : 0,
      broadcasting_start_time: startTime,
    }
  }
)

export const getWebinarTitle = createSelector(
  (state) => state.webinar.webinarInfo.name,
  (name) => name
)

export const getRemoteAudioTracksById = createSelector(
  [getTracks],
  (tracksByParticipantId) => {
    return transform(tracksByParticipantId, (acc, tracks, participantId) => {
      const audioTrack = tracks.find((track) => track.kind === 'audio')

      if (audioTrack) {
        acc[participantId] = audioTrack
      }
    })
  }
)

export const getLocalAudioTracks = createSelector(
  [getLocalTracksState],
  (tracks) => {
    return tracks.filter((track) => track.kind === 'audio')
  }
)

export const isLocalAudioMuted = createSelector(
  [getLocalAudioTracks],
  (audioTracks) => {
    return audioTracks.length > 0 && audioTracks[0].isEnabled === false
  }
)

export const getMutedParticipantIds = createSelector(
  [getTracks, isLocalAudioMuted],
  (tracksByParticipant, localParticipantMuted) => {
    const mutedParticipantIds = reduce(
      tracksByParticipant,
      (acc, tracks, participantId) => {
        if (some(tracks, { kind: 'audio', isEnabled: false })) {
          acc.add(participantId)
        }

        return acc
      },
      new Set()
    )

    if (localParticipantMuted) {
      mutedParticipantIds.add('local')
    }

    return mutedParticipantIds
  }
)

export const getBroadcastingTrackType = createSelector(
  [
    getSelectedPresenterTrackId,
    getSelectedPresenterParticipantId,
    getTracks,
    getLocalTracksState,
  ],
  (trackId, participantId, tracksByParticipant, localTracks) => {
    let track

    if (participantId === 'local') {
      track = localTracks.find((x) => x.id === trackId)
    } else if (isArray(tracksByParticipant[participantId])) {
      track = tracksByParticipant[participantId].find((x) => x.id === trackId)
    }

    return get(track, 'name')
  }
)

export const getEndButtonState = createSelector(
  [getEndingWebinarState],
  (isEndingWebinar) => {
    return isEndingWebinar ? 'loading' : 'idle'
  }
)

export const getLiveButtonState = createSelector(
  [getLiveTriggeredTimestamp],
  (liveTogglingTriggeredAt) => {
    return isNull(liveTogglingTriggeredAt) ? 'idle' : 'loading'
  }
)

const getMetricForItem = (metrics) => (item) => {
  const metricObj = find(metrics, (metric) => metric.metric === item)

  if (isEmpty(metricObj)) {
    return undefined
  }

  return webinarPerformanceOrdinalFormatter(metricObj.value, 'Best Webinar')
}

export const getWebinarOverMetrics = createSelector(
  [getFunnelData, getMetricsData],
  (funnel, metrics) => {
    const registrantsValue = get(funnel, 'conversions_fmt', '0')
    const attendanceRate = get(funnel, 'view_rate_fmt', '0%')
    const attendees = get(funnel, 'views_fmt', '0')
    const avgEngagement = get(funnel, 'engagement_per_view_fmt', '0s')

    const getMetricPosition = getMetricForItem(metrics)

    const registrationPosition = getMetricPosition(
      'rank_by_conversions_within_format'
    )
    const attendancePosition = getMetricPosition(
      'rank_by_view_rate_within_format'
    )
    const attendeesPosition = getMetricPosition('rank_by_viewers_within_format')

    return [
      {
        title: 'Registrants',
        subtitle: registrationPosition,
        number: registrantsValue,
      },
      {
        title: 'Attendance Rate',
        subtitle: attendancePosition,
        number: attendanceRate,
      },
      { title: 'Attendees', subtitle: attendeesPosition, number: attendees },
      { title: 'Average Engagement', number: avgEngagement },
    ]
  }
)

export const isProducerSelector = createSelector(
  [getRole],
  (role) => role === 'producer'
)

export const isPresenterAloneBeforeWebinarSelector = createSelector(
  [getParticipantIds, getRole, getLiveData],
  (participantIds, role, liveData) => {
    const notHeadlessParticipants = participantIds.filter(
      (participantId) => !includes(participantId, 'headless')
    )

    return (
      notHeadlessParticipants.length === 0 &&
      role === 'presenter' &&
      liveData.live_status === 'upcoming'
    )
  }
)

export const isHeadlessInSession = createSelector(
  [getParticipantIds],
  (participantIds) => {
    const headless = find(
      participantIds,
      (participantId) => participantId === 0
    )

    return !isUndefined(headless)
  }
)

export const isGoLiveButtonDisabled = createSelector(
  [
    isBroadcastStreaming,
    getLiveTriggeredTimestamp,
    getLiveData,
    isHeadlessInSession,
    getGearModeState,
  ],
  (
    isStreaming,
    liveTogglingTriggeredAt,
    liveData,
    isHeadlessPresent,
    isGearMode
  ) => {
    const togglingInProgress = !isNull(liveTogglingTriggeredAt)
    const isDraft = liveData.draft_p === 1
    const isHeadlessMissing = !isGearMode && !isHeadlessPresent

    return (
      isHeadlessMissing ||
      !isStreaming ||
      togglingInProgress ||
      liveData.liveStatus === 'previous' ||
      isDraft
    )
  }
)

const criticalErrorsWithNoTitle = ['PRESENTER_ALREADY_IN_SESSION']

export const getIsCriticalErrorTitleHidden = createSelector(
  [getCriticalErrorType],
  (errorType) => includes(criticalErrorsWithNoTitle, errorType)
)

export const getIsDraft = createSelector([getLiveData], (liveData) =>
  intToBool(liveData.draft_p)
)

export const getIsLiveViewersVisible = createSelector(
  [getIsDraft, isProducerSelector, isBroadcastLive],
  (isDraft, isProducer, isBroadcasting) =>
    isProducer && !isDraft && isBroadcasting
)
